// Monkey Ride 3D - Game State Management

export const GAME_STATUS = {
  IDLE: 'idle',
  PLAYING: 'playing',
  PAUSED: 'paused',
  GAME_OVER: 'game_over',
  CONTINUE: 'continue',
};

export const PLAYER_STATE = {
  RUNNING: 'running',
  JUMPING: 'jumping',
  SLIDING: 'sliding',
  FLYING: 'flying',
  CRASHED: 'crashed',
};

export function createInitialGameState() {
  return {
    status: GAME_STATUS?.IDLE,
    score: 0,
    distance: 0,
    coins: 0,
    coinBalance: loadCoinBalance(),
    highScore: loadHighScore(),
    speed: 8,
    multiplier: 1,
    player: {
      lane: 1, // 0=left, 1=center, 2=right
      targetLane: 1,
      laneTransition: 0, // 0-1 progress
      state: PLAYER_STATE?.RUNNING,
      jumpHeight: 0,
      jumpVelocity: 0,
      slideTimer: 0,
      flyTimer: 0,
      invincible: 0,
      characterId: loadSelectedCharacter(),
      vehicleId: loadSelectedVehicle(),
    },
    activePowerUps: {},
    obstacles: [],
    coinObjects: [],
    powerUpObjects: [],
    roadSegments: [],
    frame: 0,
    sessionCoins: 0,
    comboChain: 0,
    comboTimer: 0,
    nearMissCount: 0,
    environment: loadSelectedEnvironment(),
    missions: loadMissions(),
    weeklyScore: loadWeeklyScore(),
  };
}

export function loadHighScore() {
  try { return parseInt(localStorage.getItem('monkeyride_highscore') || '0'); } catch { return 0; }
}

export function saveHighScore(score) {
  try { localStorage.setItem('monkeyride_highscore', String(score)); } catch {}
}

export function loadCoinBalance() {
  try { return parseInt(localStorage.getItem('monkeyride_coins') || '0'); } catch { return 0; }
}

export function saveCoinBalance(coins) {
  try { localStorage.setItem('monkeyride_coins', String(coins)); } catch {}
}

export function loadSelectedCharacter() {
  try { return localStorage.getItem('monkeyride_character') || 'classic'; } catch { return 'classic'; }
}

export function saveSelectedCharacter(id) {
  try { localStorage.setItem('monkeyride_character', id); } catch {}
}

export function loadSelectedVehicle() {
  try { return localStorage.getItem('monkeyride_vehicle') || 'scooter'; } catch { return 'scooter'; }
}

export function saveSelectedVehicle(id) {
  try { localStorage.setItem('monkeyride_vehicle', id); } catch {}
}

export function loadSelectedEnvironment() {
  try { return localStorage.getItem('monkeyride_env') || 'jungle'; } catch { return 'jungle'; }
}

export function loadUnlockedItems() {
  try {
    const data = localStorage.getItem('monkeyride_unlocked');
    return data ? JSON.parse(data) : { characters: ['classic'], vehicles: ['scooter'] };
  } catch { return { characters: ['classic'], vehicles: ['scooter'] }; }
}

export function saveUnlockedItems(items) {
  try { localStorage.setItem('monkeyride_unlocked', JSON.stringify(items)); } catch {}
}

export function loadMissions() {
  const defaults = [
    { id: 'coins_100', name: 'Coin Collector', desc: 'Collect 100 coins in one run', target: 100, progress: 0, reward: 200, completed: false, type: 'coins' },
    { id: 'distance_500', name: 'Road Warrior', desc: 'Travel 500m in one run', target: 500, progress: 0, reward: 150, completed: false, type: 'distance' },
    { id: 'no_crash', name: 'Perfect Run', desc: 'Run 200m without crashing', target: 200, progress: 0, reward: 300, completed: false, type: 'no_crash' },
    { id: 'powerup_3', name: 'Power Player', desc: 'Collect 3 power-ups in one run', target: 3, progress: 0, reward: 100, completed: false, type: 'powerups' },
    { id: 'score_1000', name: 'High Scorer', desc: 'Reach a score of 1000', target: 1000, progress: 0, reward: 250, completed: false, type: 'score' },
  ];
  try {
    const saved = localStorage.getItem('monkeyride_missions');
    return saved ? JSON.parse(saved) : defaults;
  } catch { return defaults; }
}

export function saveMissions(missions) {
  try { localStorage.setItem('monkeyride_missions', JSON.stringify(missions)); } catch {}
}

export function loadWeeklyScore() {
  try {
    const data = localStorage.getItem('monkeyride_weekly');
    if (!data) return { score: 0, week: getCurrentWeek() };
    const parsed = JSON.parse(data);
    if (parsed?.week !== getCurrentWeek()) return { score: 0, week: getCurrentWeek() };
    return parsed;
  } catch { return { score: 0, week: getCurrentWeek() }; }
}

export function saveWeeklyScore(score) {
  try {
    const week = getCurrentWeek();
    localStorage.setItem('monkeyride_weekly', JSON.stringify({ score, week }));
  } catch {}
}

export function getCurrentWeek() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return Math.ceil(((now - start) / 86400000 + start?.getDay() + 1) / 7);
}

export function loadDailyReward() {
  try {
    const data = localStorage.getItem('monkeyride_daily');
    if (!data) return { lastClaim: null, streak: 0 };
    return JSON.parse(data);
  } catch { return { lastClaim: null, streak: 0 }; }
}

export function saveDailyReward(data) {
  try { localStorage.setItem('monkeyride_daily', JSON.stringify(data)); } catch {}
}

export function canClaimDailyReward() {
  const { lastClaim } = loadDailyReward();
  if (!lastClaim) return true;
  const last = new Date(lastClaim);
  const now = new Date();
  return now?.toDateString() !== last?.toDateString();
}

export function getDailyRewardAmount(streak) {
  const base = 50;
  return base + Math.min(streak, 6) * 25; // 50, 75, 100, 125, 150, 175, 200
}

export function loadLeaderboard() {
  const defaults = [
    { name: 'MonkeyKing', score: 15420, avatar: '🐒' },
    { name: 'SpeedRunner', score: 12800, avatar: '⚡' },
    { name: 'CoinMaster', score: 11200, avatar: '🪙' },
    { name: 'NinjaApe', score: 9800, avatar: '🥷' },
    { name: 'GoldenRider', score: 8500, avatar: '✨' },
    { name: 'JungleKing', score: 7200, avatar: '🌿' },
    { name: 'SpaceMonk', score: 6100, avatar: '🚀' },
    { name: 'PirateApe', score: 5400, avatar: '🏴‍☠️' },
  ];
  try {
    const saved = localStorage.getItem('monkeyride_leaderboard');
    return saved ? JSON.parse(saved) : defaults;
  } catch { return defaults; }
}

export function updateLeaderboard(playerName, score) {
  const board = loadLeaderboard();
  const existing = board?.findIndex(e => e?.name === playerName);
  if (existing >= 0) {
    if (score > board?.[existing]?.score) board[existing].score = score;
  } else {
    board?.push({ name: playerName, score, avatar: '🐒' });
  }
  board?.sort((a, b) => b?.score - a?.score);
  const top10 = board?.slice(0, 10);
  try { localStorage.setItem('monkeyride_leaderboard', JSON.stringify(top10)); } catch {}
  return top10;
}
